import React, { useState } from 'react';
import { MdAdd, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { premadeModes, PremadeMode } from './premadeModes';
import { CustomMode } from './types';

interface PremadeModeSuggestionsProps {
    existingModes: Record<string, CustomMode>;
    onSelectPremadeMode: (mode: PremadeMode) => void;
}

const PremadeModeSuggestions: React.FC<PremadeModeSuggestionsProps> = ({
    existingModes,
    onSelectPremadeMode
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Filter out modes that already exist (case-insensitive comparison)
    const existingModeNames = Object.keys(existingModes).map(key => key.toLowerCase());
    const availablePremadeModes = premadeModes.filter(
        mode => !existingModeNames.includes(mode.name.toLowerCase())
    );

    if (availablePremadeModes.length === 0) {
        return null;
    }

    return (
        <div className="border border-neutral-300 dark:border-neutral-600 rounded-lg overflow-hidden">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors text-left"
            >
                <div className="flex items-center space-x-2">
                    <span className="text-sm dark:text-neutral-300 font-medium">
                        Quick Add Templates
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-300 rounded-full">
                        {availablePremadeModes.length}
                    </span>
                </div>
                {isExpanded ? (
                    <MdExpandLess size={20} className="text-neutral-500 dark:text-neutral-400" />
                ) : (
                    <MdExpandMore size={20} className="text-neutral-500 dark:text-neutral-400" />
                )}
            </button>

            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
                <div className="border-t border-neutral-300 dark:border-neutral-600 bg-neutral-25 dark:bg-neutral-850">
                    <div className="p-3 space-y-2">
                        {availablePremadeModes.map((mode) => (
                            <div
                                key={mode.name}
                                onClick={() => onSelectPremadeMode(mode)}
                                className="group cursor-pointer p-2 bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded border border-neutral-200 dark:border-neutral-700 hover:border-cyan-300 dark:hover:border-cyan-600 transition-all duration-150"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium dark:text-neutral-200 truncate">
                                                {mode.name}
                                            </span>
                                            {mode.preferredModel && (
                                                <span className="text-xs px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-600 text-neutral-600 dark:text-neutral-300 rounded text-nowrap">
                                                    {mode.preferredModel}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-1">
                                            {mode.description}
                                        </p>
                                    </div>
                                    <MdAdd
                                        size={14}
                                        className="text-cyan-600 group-hover:text-cyan-500 transition-colors ml-2 flex-shrink-0"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PremadeModeSuggestions;